import type { Asset, FileType } from '@scio3/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import {
  FILE_TYPES,
  ASSET_STATUS,
  ASSET_TYPES,
  WEB_TYPES,
  COMMON_TYPE,
  IMAGE_MIME_TYPES,
  VIDEO_MIME_TYPES,
  DOCUMENT_MIME_TYPES,
  PDF_MIME_TYPE,
  APP_TYPES,
  APP_REJECT_ASSIGN,
  FILE_ASSET_STATUS,
} from '@scio3/shared-types';
import { getSignedUrl } from '@/lib/file-cdn';
import { getAssetDef, isAssetReady, isAssetWaiting } from './app-meta';

/** YouTube and Vimeo web types that show the Duration field like file assets. */
const DURATION_WEB_TYPES = new Set<string>([WEB_TYPES.YOUTUBE, WEB_TYPES.VIMEO]);

export function getFileTypeLabel(fileType?: string): string {
  switch (fileType) {
    case FILE_TYPES.IMAGE:
      return 'Images';
    case FILE_TYPES.VIDEO:
      return 'Videos';
    case FILE_TYPES.AUDIO:
      return 'Audio';
    case FILE_TYPES.DOCUMENT:
      return 'Docs';
    case FILE_TYPES.WEB:
      return 'Apps';
    default:
      return 'File';
  }
}

export function getAssetDisplayName(asset: Asset): string {
  return asset.originalFileName || asset.appType || asset.webType || 'Untitled';
}

export function formatFileSize(bytes?: number): string {
  if (!bytes || bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / Math.pow(1024, i);
  return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export function getAssetTypeColor(fileType?: FileType | string): string {
  switch (fileType) {
    case FILE_TYPES.IMAGE:
      return 'bg-type-image-bg text-type-image border-type-image-border';
    case FILE_TYPES.VIDEO:
      return 'bg-type-video-bg text-type-video border-type-video-border';
    case FILE_TYPES.AUDIO:
      return 'bg-type-audio-bg text-type-audio border-type-audio-border';
    case FILE_TYPES.DOCUMENT:
      return 'bg-type-doc-bg text-type-doc border-type-doc-border';
    case FILE_TYPES.WEB:
      return 'bg-type-app-bg text-type-app border-type-app-border';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

export async function getAssetThumbnailUrl(asset: Asset): Promise<string | null> {
  if (asset.thumbnail) {
    if (asset.thumbnail.startsWith('http')) {
      return asset.thumbnail;
    }
    // S3 key → signed CDN URL (falls back to direct S3 if CDN not configured)
    return getSignedUrl(asset.thumbnail, asset.bucket);
  }
  return null;
}

/** Build a signed URL to an S3-hosted asset from its AWSS3ID key. */
export async function getDirectAssetUrl(
  awsKey?: string,
  bucket?: string | null,
): Promise<string | null> {
  if (!awsKey) return null;
  if (awsKey.startsWith('http')) return awsKey;
  return getSignedUrl(awsKey, bucket);
}

/**
 * Determines if an asset supports live/expire scheduling.
 * Uses backend-driven `getAssetDef(asset).canExpire` which accounts for
 * asset type, status overrides (CONVERTING/WAITING), and per-app-type flags.
 */
export function canAssetExpire(asset: Asset): boolean {
  return getAssetDef(asset).canExpire === true;
}

/**
 * Determines if an asset can be previewed.
 * Uses `getAssetDef(asset).canPreview` with additional white-label check.
 * Replaces inline canPreview logic in AssetCard/AssetRow.
 */
export function canPreviewAsset(asset: Asset, isWhiteLabel = false): boolean {
  if (isWhiteLabel) return false;
  return getAssetDef(asset).canPreview === true;
}

/** File types AssetPreviewDialog renders natively (img / video / doc-page carousel). */
const INLINE_PREVIEWABLE_FILE_TYPES = new Set<string>([
  FILE_TYPES.IMAGE,
  FILE_TYPES.VIDEO,
  FILE_TYPES.DOCUMENT,
]);

/**
 * Determines if an asset can be previewed from an inline row/card action.
 *
 * Wider than `canPreviewAsset`: uploaded media has AppMeta `canPreview: false` (scio1 parity,
 * see the hardcoded file-type defs in app-meta.ts), yet AssetPreviewDialog renders all three
 * natively via its isImage / isVideo / isDocument branches. This predicate opens that path for
 * the inline Preview button.
 *
 * AUDIO IS EXCLUDED ON PURPOSE — the dialog has no <audio> branch, so an audio asset would open
 * to "No preview available for this asset type." A button that opens a dead dialog is worse than
 * no button. Do not "simplify" this to all file types.
 *
 * `canPreviewAsset` is intentionally left narrow — SelectAssetModal and other AppMeta-driven
 * surfaces depend on the existing behaviour.
 */
export function canPreviewInline(asset: Asset, isWhiteLabel = false): boolean {
  if (canPreviewAsset(asset, isWhiteLabel)) return true;
  if (isWhiteLabel) return false;
  // Same status gate getAssetDef applies to the AppMeta path — a CONVERTING or WAITING asset has
  // no renderable media yet.
  if (isAssetWaiting(asset) || !isAssetReady(asset)) return false;
  return INLINE_PREVIEWABLE_FILE_TYPES.has(asset.fileType ?? '');
}

/** Determines if an asset can be downloaded. Checks both asset type and company limitation. */
export function canDownloadAsset(asset: Asset): boolean {
  const company = useAuthStore.getState().company;
  if (company?.limitation?.disableDownload) return false;
  return getAssetDef(asset).canDownload === true;
}

/** Determines if an asset can be duplicated (cloned). */
export function canCloneAsset(asset: Asset): boolean {
  return getAssetDef(asset).canClone === true;
}

/** Determines if an asset can be moved to another folder. */
export function canMoveAsset(asset: Asset): boolean {
  return getAssetDef(asset).canMoveTo !== false;
}

/** Determines if an asset can be added to a playlist. */
export function canAssignToPlaylist(asset: Asset): boolean {
  return getAssetDef(asset).canAssignPlaylist === true;
}

/** Determines if the "Copy Asset ID" action should be shown (matches scio1/scio2 appMeta.copyAssetId). */
export function canCopyAssetId(asset: Asset): boolean {
  return getAssetDef(asset).copyAssetId === true;
}

/** Max file size for asset replacement: 1 GB */
export const MAX_REPLACE_FILE_SIZE = 1073741824; // 1 GB in bytes

/**
 * File type extensions accepted for replacement, keyed by original asset fileType.
 * Matches scio1's IMAGE_TYPES, VIDEO_TYPES, CONVERT_PDF_TYPES + TYPE_PDF.
 */
const REPLACE_FILE_EXTENSIONS: Record<string, string> = {
  [FILE_TYPES.IMAGE]: '.jpg,.jpeg,.png,.gif,.bmp,.svg',
  [FILE_TYPES.VIDEO]: '.mp4,.3gp,.m4v,.webm,.avi,.mov,.mpeg,.wmv',
  [FILE_TYPES.DOCUMENT]: '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.odt,.ods',
};

/**
 * Returns the accepted MIME types array for a given asset fileType.
 * Used to pass fileTypes in the getUploadOptions payload for backend validation.
 */
export function getReplaceMimeTypes(fileType?: string): string[] {
  switch (fileType) {
    case FILE_TYPES.IMAGE:
      return [...IMAGE_MIME_TYPES];
    case FILE_TYPES.VIDEO:
      return [...VIDEO_MIME_TYPES];
    case FILE_TYPES.DOCUMENT:
      return [PDF_MIME_TYPE, ...DOCUMENT_MIME_TYPES];
    default:
      return [];
  }
}

/**
 * Returns the HTML accept attribute value for the file input based on the original asset's fileType.
 * Returns undefined if no restriction applies (fallback: accept any file).
 */
export function getReplaceAcceptAttribute(fileType?: string): string | undefined {
  if (!fileType) return undefined;
  return REPLACE_FILE_EXTENSIONS[fileType];
}

/**
 * `commonType`s whose "Replace Asset" flows through the asset's own editor instead of the
 * generic Uppy uploader.
 *
 * scio1 parity (#1845): `assetList.select()` only opens the generic uploader when the asset
 * has NO `commonType` — everything else falls through to `FileService.openAssetModal`:
 *
 *   if ([IMAGE, VIDEO, DOCUMENT].includes(fileType) && !commonType && !isDisable) {
 *     openUploader(...); return;      // generic
 *   }
 *   FileService.openAssetModal(asset); // the app's own editor
 *
 * scio3 sent every replaceable file asset to the generic uploader, so Canva Import assets
 * (`type: 'file'` + `commonType: 'canvaImport'`) lost their Canva replace UI.
 *
 * This set is also the ONLY reason an app-backed asset still shows "Replace Asset" at all —
 * `canReplaceAsset` hides the item for every other `commonType` (#1971). Add an entry only
 * when the app owns a replace UI of its own; adding one re-exposes the menu item.
 */
const REPLACE_VIA_ASSET_EDITOR = new Set<string>([COMMON_TYPE.CANVA_IMPORT]);

/**
 * Determines if a file-type asset can be replaced.
 *
 * Replace Asset is a **direct-upload-only** action: it swaps the stored file for one picked off
 * the user's machine. App-backed assets get their content from an authenticated source, so the
 * action must not be offered for them (#1971).
 *
 * #1971 — Google Slides and PowerPoint assets are stored as `type: 'file'` with a `document`
 * `fileType`, and the old gate excluded only `screenCaptureApp`. So they passed, and a direct
 * upload produced a hybrid asset: the portal thumbnail showed the upload while the device kept
 * playing the app (PowerPoint), the config dialog still opened the authenticated app view, and
 * Google Slides' Refresh silently reverted the replacement. The same hole was open for every
 * other file-backed `commonType` (`googleSheets`, `googleDocs`, `microsoftExcel`,
 * `microsoftExcelDataSource`, `AeriCast`, `qrOverlayFile`, `videoWall`, …).
 *
 * The rule is therefore "plain uploaded file, or an app that owns its own replace UI" —
 * `REPLACE_VIA_ASSET_EDITOR` (today: Canva Import, #1845), which routes to the app's editor
 * rather than the generic uploader and so never produces a hybrid. `screenCaptureApp` is a
 * `commonType` outside that set, so its old explicit exclusion is subsumed here.
 *
 * Note: Permission check (canEdit('asset')) is handled separately by the caller.
 */
export function canReplaceAsset(asset: Asset): boolean {
  // Must be a file-type asset
  if (asset.type !== ASSET_TYPES.FILE) return false;

  // fileType must be IMAGE, VIDEO, or DOCUMENT
  const replaceableFileTypes: string[] = [FILE_TYPES.IMAGE, FILE_TYPES.VIDEO, FILE_TYPES.DOCUMENT];
  if (!asset.fileType || !replaceableFileTypes.includes(asset.fileType)) return false;

  // App-backed asset: only offered when the app owns its own replace UI (#1971, #1845).
  if (asset.commonType && !REPLACE_VIA_ASSET_EDITOR.has(asset.commonType)) return false;

  // Must not be converting (ASSET_STATUS.CONVERTING = 'converting')
  if (asset.status === ASSET_STATUS.CONVERTING) return false;

  // Must not be disabled
  if (asset.isDisable) return false;

  return true;
}

/**
 * Whether "Replace Asset" should open the asset's own editor instead of the generic uploader.
 *
 * Decided from the **asset alone** — no appMeta/registry lookup, so it needs nothing loaded
 * beyond the asset the list query already returned.
 */
export function replaceOpensAssetEditor(asset: Asset): boolean {
  return !!asset.commonType && REPLACE_VIA_ASSET_EDITOR.has(asset.commonType);
}

/**
 * Format user info for display in asset info dialog.
 * Backend returns { fullName, userEmail } from resolveAssetData.
 * Returns "Full Name (email)" or just email/name if partial.
 */
export function formatUserInfo(
  userInfo?: {
    fullName?: string;
    userEmail?: string;
  } | null,
): string | null {
  if (!userInfo) return null;
  const { fullName, userEmail } = userInfo;
  const name = fullName?.trim();
  if (name && userEmail) return `${name} (${userEmail})`;
  if (name) return name;
  if (userEmail) return userEmail;
  return null;
}

/**
 * Format duration in seconds to a human-readable time string.
 * Matches scio1's convertSecondsToTimeFormat behavior.
 * Examples: 90 -> "1m 30s", 3661 -> "1h 1m 1s", 10 -> "10s"
 */
export function formatDuration(totalSeconds?: number): string | null {
  if (!totalSeconds || totalSeconds <= 0) return null;

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);
  return parts.join(' ');
}

/**
 * Determines whether the Duration field should be visible for an asset.
 * scio1 logic: file assets show duration, YouTube/Vimeo web assets show duration,
 * all other asset types hide duration.
 */
export function shouldShowDuration(asset: Asset): boolean {
  if (asset.type === ASSET_TYPES.FILE) return true;
  if (asset.webType && DURATION_WEB_TYPES.has(asset.webType)) return true;
  return false;
}

/**
 * Determines whether file-specific fields (Type, File Type, File Size) should show.
 * scio1 logic: only shown for type === 'file'.
 */
export function isFileAsset(asset: Asset): boolean {
  return asset.type === ASSET_TYPES.FILE;
}

/** appTypes allowed to open while CONVERTING. Empty = block all (scio1 parity). */
const CONVERTING_OPEN_ALLOWLIST: string[] = [];

/**
 * Whether an asset's detail can be opened. Blocks all asset types while CONVERTING
 * (scio1 `select()` parity), except appTypes in CONVERTING_OPEN_ALLOWLIST.
 */
export function canOpenAssetDetail(asset: Asset): boolean {
  if (asset.status !== FILE_ASSET_STATUS.CONVERTING) return true;
  return CONVERTING_OPEN_ALLOWLIST.includes(asset.appType ?? '');
}

/**
 * Determines whether an asset can be pushed to screens.
 *
 * Based on scio1's `isAllowPushToScreen()` logic:
 * - Asset type must not be TEMPLATE or DATASOURCE
 * - Audio files cannot be pushed
 * - Certain common types are rejected (QR, emergencyMessage, emergencyFeed, flashMessage)
 * - Certain app types are rejected (virtualScreen, AI, data sources, capAlert, sensor)
 */
export function canPushToScreen(asset: Asset): boolean {
  // Assets still being processed cannot be pushed
  if (asset.status === FILE_ASSET_STATUS.CONVERTING) return false;

  // TEMPLATE and DATASOURCE asset types cannot be pushed
  if (asset.type === ASSET_TYPES.TEMPLATE || asset.type === ASSET_TYPES.DATASOURCE) return false;

  // Audio files cannot be pushed
  if (asset.fileType === FILE_TYPES.AUDIO) return false;

  // Reject certain common types (QR, emergencyMessage, emergencyFeed, flashMessage)
  const rejectCommonTypes: readonly string[] = [
    COMMON_TYPE.QR,
    APP_TYPES.EMERGENCY_MESSAGE,
    APP_TYPES.EMERGENCY_FEED,
    APP_TYPES.FLASH_MESSAGE,
  ];
  if (asset.commonType && rejectCommonTypes.includes(asset.commonType)) return false;

  // Reject certain app types (virtualScreen, AI, data sources, capAlert, sensor)
  if (asset.appType && (APP_REJECT_ASSIGN as readonly string[]).includes(asset.appType))
    return false;

  return true;
}

/**
 * Determines whether the Scale field should be visible in push-to-screens dialog.
 * scio1 only shows scale for image, video, document files and Canvas appType.
 */
export function shouldShowScale(asset: Asset): boolean {
  if (asset.fileType === FILE_TYPES.IMAGE) return true;
  if (asset.fileType === FILE_TYPES.VIDEO) return true;
  if (asset.fileType === FILE_TYPES.DOCUMENT) return true;
  if (asset.appType === APP_TYPES.CANVAS) return true;
  return false;
}

/**
 * Determines whether an asset should show emergency labels (Expire/Starts in).
 *
 * Based on scio-v2's `isShowEmergency()` logic:
 * - CAP_ALERT + (FLASHING | FINISHED | WAITING)
 * - FLASH_MESSAGE or EMERGENCY_MESSAGE + (ACTIVE | PENDING)
 */
export function isEmergencyAsset(asset: Asset): boolean {
  const status = asset.status as string | undefined;

  if (
    asset.appType === APP_TYPES.CAP_ALERT &&
    (status === FILE_ASSET_STATUS.FLASHING ||
      status === FILE_ASSET_STATUS.FINISHED ||
      status === FILE_ASSET_STATUS.WAITING)
  ) {
    return true;
  }

  if (
    (asset.appType === APP_TYPES.FLASH_MESSAGE || asset.appType === APP_TYPES.EMERGENCY_MESSAGE) &&
    (status === FILE_ASSET_STATUS.ACTIVE || status === FILE_ASSET_STATUS.PENDING)
  ) {
    return true;
  }

  return false;
}

/**
 * Calculate the "Expire in X" / "Expired X ago" / "Start in X" label for
 * Flash Message, CAP Alert, and Emergency Message assets.
 *
 * Ports scio1's `getExpireDate()` logic using `currentDate` + `duration`/`emergencyDuration`.
 * Returns { label, endDate } or null if not applicable.
 */
export type EmergencyExpireKind = 'scheduled' | 'expiring' | 'expired' | 'live';

export interface EmergencyExpireInfo {
  label: string;
  kind: EmergencyExpireKind;
  /** @deprecated prefer `kind === 'expired'` */
  isExpired: boolean;
}

export function getEmergencyExpireInfo(
  asset: Asset,
  t: (key: string) => string,
): EmergencyExpireInfo | null {
  if (!isEmergencyAsset(asset)) return null;

  const status = asset.status as string | undefined;
  const contentStatus = asset.contentStatus as string | undefined;

  // CAP Alert waiting/finished with active content — show "Waiting"
  if (
    asset.appType === APP_TYPES.CAP_ALERT &&
    contentStatus === FILE_ASSET_STATUS.ACTIVE &&
    (status === FILE_ASSET_STATUS.WAITING || status === FILE_ASSET_STATUS.FINISHED)
  ) {
    return { label: t('Waiting'), kind: 'scheduled', isExpired: false };
  }

  // Inactive
  if (contentStatus === FILE_ASSET_STATUS.INACTIVE) {
    return { label: t('Inactive'), kind: 'expired', isExpired: false };
  }

  const emergencyAppTypes: string[] = [APP_TYPES.FLASH_MESSAGE, APP_TYPES.EMERGENCY_MESSAGE];
  const duration = emergencyAppTypes.includes(asset.appType as string)
    ? asset.duration
    : asset.emergencyDuration;

  // Permanent (active with explicit zero/negative duration)
  // Note: scio1 uses `duration <= 0` which is false for undefined — falls through to time calc
  if (status === FILE_ASSET_STATUS.ACTIVE && duration != null && duration <= 0) {
    return { label: t('Permanent'), kind: 'live', isExpired: false };
  }

  // Pending with startDate
  const pendingStart = asset.startDate ?? asset.assetExpired?.startDate;
  if (status === FILE_ASSET_STATUS.PENDING && pendingStart) {
    const startDate = new Date(pendingStart);
    if (startDate > new Date()) {
      return {
        label: `${t('Start')} ${_relativeTime(startDate)}`,
        kind: 'scheduled',
        isExpired: false,
      };
    }
    return null;
  }

  // Calculate end date from currentDate (or startDate fallback) + duration.
  //
  // Defensive guard: without a real base date AND a real duration, the fallback
  // of `new Date() + 0` produces `endDate ≈ now`, which the relative-time
  // formatter renders as "in 1 minute" — misleading text instead of no label.
  // Render nothing in that case; better silent than wrong (#1053).
  const baseDate = asset.currentDate ?? asset.startDate;
  if (!baseDate || duration == null) {
    return null;
  }
  const endDate = new Date(new Date(baseDate).getTime() + duration * 1000);
  const now = new Date();

  if (endDate < now) {
    return {
      label: `${t('Expired')} ${_relativeTime(endDate)}`,
      kind: 'expired',
      isExpired: true,
    };
  }
  return {
    label: `${t('Expire')} ${_relativeTime(endDate)}`,
    kind: 'expiring',
    isExpired: false,
  };
}

/** Simple relative time like "in 5 hours" or "3 hours ago". */
function _relativeTime(date: Date): string {
  const diffMs = date.getTime() - Date.now();
  const absDiff = Math.abs(diffMs);
  const minutes = Math.round(absDiff / 60_000);
  const hours = Math.round(absDiff / 3_600_000);
  const days = Math.round(absDiff / 86_400_000);

  let unit: string;
  if (minutes < 2) unit = 'in 1 minute';
  else if (minutes < 60) unit = `in ${minutes} minutes`;
  else if (hours < 2) unit = 'in 1 hour';
  else if (hours < 24) unit = `in ${hours} hours`;
  else if (days < 2) unit = 'in 1 day';
  else unit = `in ${days} days`;

  // Past: flip "in X" to "X ago"
  if (diffMs < 0) return unit.replace('in ', '') + ' ago';
  return unit;
}
